import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Modal,
  IconButton,
} from "@mui/material";
import { Field, Form } from "react-final-form";
import { Select as RffSelect } from "mui-rff";
import axios from "axios";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";

const API_KEY = "AIzaSyCKfUSr5PacGOhWURYgZlgjmgGh4Oc5kjE";

type VideoItem = {
  id?: number | string;
  title?: string;
  description?: string;
  url: string;
  thumbnail?: string;
  platform?: string;
  duration?: string;
  type?: string;
};

type CurrentCategory = {
  videos: VideoItem[];
};

type PlaylistModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  playlistId: number | string;
  currentCategory: CurrentCategory;
};

type VideoDetails = {
  title: string;
  description: string;
  thumbnail: string;
  duration: string;
};

const initialVideoDetails: VideoDetails = {
  title: "",
  description: "",
  thumbnail: "",
  duration: "",
};

const isValidYouTubeUrl = (url: string) => {
  const pattern =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})\b/;
  return pattern.test(url);
};

const isValidTikTokUrl = (url: string) => {
  return /^(https?:\/\/)?(www\.)?tiktok\.com\/.+/i.test(url);
};

const parseDuration = (isoDuration: string) => {
  const match = isoDuration.match(
    /PT(?:([\d]+)H)?(?:([\d]+)M)?(?:([\d]+)S)?/
  );

  if (!match) return "00:00:00";

  const hours = parseInt(match[1] || "0", 10);
  const minutes = parseInt(match[2] || "0", 10);
  const seconds = parseInt(match[3] || "0", 10);

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = seconds.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

const extractVideoId = (url: string) => {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const getVideoDetails = async (videoId: string) => {
  const url = `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${API_KEY}`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.items?.length > 0) {
      const videoData = data.items[0];
      return {
        title: videoData.snippet?.title || "",
        description: videoData.snippet?.description || "",
        thumbnail: videoData.snippet?.thumbnails?.high?.url || "",
        duration: parseDuration(videoData.contentDetails?.duration || "PT0S"),
      };
    }

    throw new Error("Video not found");
  } catch (error) {
    console.error("Error fetching video details:", error);
    return null;
  }
};

const PlaylistModal: React.FC<PlaylistModalProps> = ({
  open,
  onClose,
  onSubmit,
  playlistId,
  currentCategory,
}) => {
  const initialFormValues = {
    title: "",
    description: "",
    url: "",
    tiktokCopyUrl: "",
    instagramReelUrl: "",
    platform: "youtube",
  };

  const [formValues, setFormValues] = useState(initialFormValues);
  const [videoDetails, setVideoDetails] =
    useState<VideoDetails>(initialVideoDetails);
  const [s3Videos, setS3Videos] = useState<VideoItem[]>([]);
  const [hoveredVideo, setHoveredVideo] = useState<string | null>(null);
  const [selectedVideos, setSelectedVideos] = useState<string[]>([]);
  const [url, setUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVideoId, setModalVideoId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      resetLocalState();
    }
  }, [open]);

  const resetLocalState = () => {
    setFormValues(initialFormValues);
    setVideoDetails(initialVideoDetails);
    setS3Videos([]);
    setHoveredVideo(null);
    setSelectedVideos([]);
    setUrl("");
    setSearchQuery("");
    setSearchResults([]);
    setNextPageToken(null);
    setShowResults(false);
    setIsLoading(false);
    setModalOpen(false);
    setModalVideoId(null);
  };

  const resetAndClose = (form?: any) => {
    if (form) {
      form.reset();
      form.change("url", "");
      form.change("title", "");
      form.change("description", "");
      form.change("tiktokCopyUrl", "");
      form.change("instagramReelUrl", "");
      form.change("platform", "youtube");
    }

    resetLocalState();
    onClose();
  };

  const validate = (values: any) => {
    const errors: Record<string, string> = {};

    if (values.platform === "youtube") {
      if (!values.url?.trim()) {
        errors.url = "Required";
      } else if (!isValidYouTubeUrl(values.url.trim())) {
        errors.url = "Invalid YouTube URL";
      }

      if (!values.title?.trim()) {
        errors.title = "Required";
      }
    }

    if (values.platform === "tk") {
      if (!values.tiktokCopyUrl?.trim()) {
        errors.tiktokCopyUrl = "Required";
      } else if (!isValidTikTokUrl(values.tiktokCopyUrl.trim())) {
        errors.tiktokCopyUrl = "Invalid TikTok URL";
      }

      if (!values.title?.trim()) {
        errors.title = "Required";
      }
    }

    return errors;
  };

  const handleScrubVideoDetails = async (youtubeUrl: string, form: any) => {
    const videoId = extractVideoId(youtubeUrl);

    if (!videoId) {
      toast.error("Invalid YouTube URL.");
      return;
    }

    const details = await getVideoDetails(videoId);

    if (!details) {
      toast.error("Failed to fetch video details.");
      return;
    }

    setVideoDetails(details);
    form.change("title", details.title);
    form.change("description", details.description);
    form.change("url", youtubeUrl);

    setFormValues((prev) => ({
      ...prev,
      url: youtubeUrl,
      title: details.title,
      description: details.description,
    }));
  };

  const handleScrubTikTokDetails = async (tkUrl: string, form: any) => {
    try {
      const res = await fetch(
        `https://www.tiktok.com/oembed?url=${encodeURIComponent(tkUrl)}`
      );
      const data = await res.json();

      const updatedDetails: VideoDetails = {
        title: data.title || "",
        thumbnail: data.thumbnail_url || "",
        description: data.author_name ? `By ${data.author_name}` : "",
        duration: "00:00:00",
      };

      setVideoDetails(updatedDetails);

      form.change("title", updatedDetails.title);
      form.change("description", updatedDetails.description);
      form.change("tiktokCopyUrl", tkUrl);

      setFormValues((prev) => ({
        ...prev,
        title: updatedDetails.title,
        description: updatedDetails.description,
        tiktokCopyUrl: tkUrl,
      }));
    } catch (error) {
      console.error("Failed to get TikTok details:", error);
      toast.error("Failed to fetch TikTok details.");
    }
  };

  const handleScrubInstagramDetails = async (igUrl: string, form: any) => {
    try {
      const res = await fetch(
        `https://jsonlink.io/api/extract?url=${encodeURIComponent(igUrl)}`
      );
      const data = await res.json();

      const updatedDetails: VideoDetails = {
        title: data.title || "Instagram Reel",
        thumbnail: data.images?.[0] || "",
        description: data.description || "",
        duration: "00:00:00",
      };

      setVideoDetails(updatedDetails);
      form.change("title", updatedDetails.title);
      form.change("description", updatedDetails.description);
      form.change("instagramReelUrl", igUrl);

      setFormValues((prev) => ({
        ...prev,
        title: updatedDetails.title,
        description: updatedDetails.description,
        instagramReelUrl: igUrl,
      }));
    } catch (error) {
      console.error("Failed to get Instagram details:", error);
      toast.error("Could not fetch Instagram Reel metadata.");
    }
  };

  const getS3Data = async () => {
    try {
      const response = await axios.get(`/playlist/global/get_global_playlist_items`);
      setS3Videos(response.data || []);
    } catch (error) {
      console.error("Error fetching S3 playlist items:", error);
      toast.error("Failed to fetch stock videos.");
    }
  };

  const toggleSelection = (videoUrl: string) => {
    setSelectedVideos((prevSelected) =>
      prevSelected.includes(videoUrl)
        ? prevSelected.filter((item) => item !== videoUrl)
        : [...prevSelected, videoUrl]
    );
  };

  const handleMouseEnter = (video: VideoItem) => {
    setHoveredVideo(video.url);
  };

  const handleMouseLeave = () => {
    setHoveredVideo(null);
  };

  const searchYouTubeVideos = async (query: string, pageToken: string | null = null) => {
    const baseUrl = `https://www.googleapis.com/youtube/v3/search?part=snippet&type=video&maxResults=20&q=${encodeURIComponent(
      query
    )}&key=${API_KEY}`;

    const requestUrl = pageToken ? `${baseUrl}&pageToken=${pageToken}` : baseUrl;

    try {
      const response = await fetch(requestUrl);
      const data = await response.json();

      return {
        items: data.items || [],
        nextPageToken: data.nextPageToken || null,
      };
    } catch (error) {
      console.error("YouTube Search Error:", error);
      return {
        items: [],
        nextPageToken: null,
      };
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    const { items, nextPageToken: token } = await searchYouTubeVideos(
      searchQuery.trim()
    );
    setSearchResults(items);
    setNextPageToken(token);
    setShowResults(true);
    setIsLoading(false);
  };

  const handleLoadMore = async () => {
    if (!nextPageToken) return;

    setIsLoading(true);
    const { items, nextPageToken: token } = await searchYouTubeVideos(
      searchQuery.trim(),
      nextPageToken
    );
    setSearchResults((prev) => [...prev, ...items]);
    setNextPageToken(token);
    setIsLoading(false);
  };

  const buildExistingVideosPayload = () => {
    return (currentCategory?.videos || []).map(({ id, ...video }) => ({
      title: video.title || "",
      description: video.description || "",
      url: video.url,
      thumbnail: video.thumbnail || "",
      platform: video.platform || "s3",
      duration: video.duration || "00:00:00",
      type: video.type || "mp4",
    }));
  };

  const handleSubmitYouTube = async (values: any, form: any) => {
    const videoId = extractVideoId(values.url);

    if (!videoId) {
      toast.error("Invalid YouTube URL.");
      return;
    }

    if (!values.title?.trim()) {
      form.change("title", values.title || "");
      toast.error("Title is required.");
      return;
    }

    try {
      const newVideo = {
        title: values.title.trim(),
        description: values.description || videoDetails.description || "",
        url: values.url.trim(),
        thumbnail: videoDetails.thumbnail || "",
        platform: values.platform || "youtube",
        duration: videoDetails.duration || "00:00:00",
        type: "mp4",
      };

      const updatedVideos = buildExistingVideosPayload();
      updatedVideos.push(newVideo);

      const response = await axios.put(
        `playlist/${playlistId}/save_playlist_items`,
        updatedVideos
      );

      if (response.status === 200 && response.data === true) {
        toast.success("Playlist item saved successfully");
        onSubmit(newVideo);
        resetAndClose(form);
      } else {
        toast.error("Failed to save playlist item.");
      }
    } catch (error) {
      console.error("Error saving playlist item:", error);
      toast.error("Failed to save playlist item.");
    }
  };

  const handleSubmitS3 = async (values: any, form: any) => {
    if (selectedVideos.length === 0) {
      toast.error("Please select at least one stock video.");
      return;
    }

    const selectedVideoData = s3Videos
      .filter((video) => selectedVideos.includes(video.url))
      .map(({ id, ...video }) => ({
        title: video.title || "",
        description: video.description || "",
        url: video.url,
        thumbnail: video.thumbnail || "",
        platform: video.platform || "s3",
        duration: video.duration || "00:00:00",
        type: "mp4",
      }));

    const updatedVideos = buildExistingVideosPayload();
    const playlistPayload = [...updatedVideos, ...selectedVideoData];

    try {
      const response = await axios.put(
        `playlist/${playlistId}/save_playlist_items`,
        playlistPayload
      );

      if (response.status === 200 && response.data === true) {
        toast.success("Playlist item saved successfully");
        selectedVideoData.forEach((video) => onSubmit(video));
        resetAndClose(form);
      } else {
        toast.error("Failed to save playlist item.");
      }
    } catch (error) {
      console.error("Error saving playlist item:", error);
      toast.error("Failed to save playlist item.");
    }
  };

  const handleSubmitTikTok = async (values: any, form: any) => {
    const title = values.title?.trim();
    const tkUrl = values.tiktokCopyUrl?.trim() || url.trim();

    if (!tkUrl) {
      form.change("tiktokCopyUrl", "");
      toast.error("TikTok URL is required.");
      return;
    }

    if (!isValidTikTokUrl(tkUrl)) {
      toast.error("Invalid TikTok URL.");
      return;
    }

    if (!title) {
      form.change("title", values.title || "");
      toast.error("Title is required.");
      return;
    }

    try {
      const newVideo = {
        title,
        description: values.description || videoDetails.description || "",
        url: tkUrl,
        platform: "tiktok",
        duration: videoDetails.duration || "00:00:00",
        type: "mp4",
        thumbnail: videoDetails.thumbnail || "",
      };

      const existingVideos = buildExistingVideosPayload();
      const payload = [...existingVideos, newVideo];

      const response = await axios.put(
        `/playlist/${playlistId}/save_playlist_items`,
        payload
      );

      if (response.status === 200 && response.data === true) {
        toast.success("TikTok video added to playlist successfully");
        onSubmit(newVideo);
        resetAndClose(form);
      } else {
        toast.error("Failed to add TikTok video to playlist.");
      }
    } catch (error) {
      console.error("Error adding TikTok to playlist:", error);
      toast.error("Failed to add TikTok video to playlist.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      PaperProps={{
        sx: {
          width: "950px",
          borderRadius: "15px",
          padding: "20px",
          maxWidth: "100%",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.25rem", fontWeight: "600" }}>
        Add New Playlist Item
      </DialogTitle>

      <DialogContent>
        <Form
          onSubmit={async (values, form) => {
            if (values.platform === "S3") {
              await handleSubmitS3(values, form);
              return;
            }

            if (values.platform === "youtube") {
              await handleSubmitYouTube(values, form);
              return;
            }
          }}
          validate={validate}
          initialValues={formValues}
          render={({ handleSubmit, form, values }) => (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await handleSubmit(e);
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Field name="platform">
                  {({ input }) => (
                    <RffSelect
                      {...input}
                      label="Platform"
                      required
                      formControlProps={{ margin: "normal" }}
                      onChange={(e) => {
                        const selectedPlatform = e.target.value;
                        input.onChange(e);

                        setVideoDetails(initialVideoDetails);
                        setUrl("");
                        form.change("url", "");
                        form.change("title", "");
                        form.change("description", "");
                        form.change("tiktokCopyUrl", "");
                        form.change("instagramReelUrl", "");

                        if (selectedPlatform === "S3") {
                          getS3Data();
                        }
                      }}
                      sx={{
                        backgroundColor: "#f4f6f8",
                        borderRadius: "8px",
                        padding: "8px",
                      }}
                    >
                      <MenuItem value="youtube">YouTube</MenuItem>
                      <MenuItem value="S3">Stock Videos</MenuItem>
                      <MenuItem value="tk">TikTok</MenuItem>
                    </RffSelect>
                  )}
                </Field>
              </Box>

              {values.platform === "youtube" && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                      <TextField
                        label="Search YouTube Videos"
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                        sx={{
                          backgroundColor: "#f4f6f8",
                          borderRadius: "8px",
                        }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={isLoading}
                      >
                        {isLoading ? "Searching..." : "Search"}
                      </Button>
                    </Box>

                    {showResults && searchResults.length > 0 && (
                      <>
                        <Box
                          sx={{
                            mt: 1,
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            boxShadow: 3,
                            maxHeight: 400,
                            overflowY: "auto",
                            zIndex: 10,
                            position: "relative",
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(250px, 1fr))",
                            p: 1,
                          }}
                        >
                          {searchResults.map((video) => {
                            const videoId = video.id.videoId;
                            const title = video.snippet.title;
                            const thumbnail =
                              video.snippet.thumbnails.medium.url;

                            return (
                              <Card
                                key={videoId}
                                sx={{
                                  position: "relative",
                                  cursor: "pointer",
                                  borderRadius: 2,
                                  boxShadow: 2,
                                  transition: "transform 0.2s",
                                  "&:hover": {
                                    transform: "scale(1.02)",
                                    boxShadow: 4,
                                  },
                                }}
                                onClick={async () => {
                                  const fullUrl = `https://www.youtube.com/watch?v=${videoId}`;
                                  setUrl(fullUrl);

                                  const details = await getVideoDetails(videoId);
                                  if (details) {
                                    setVideoDetails(details);
                                    form.change("url", fullUrl);
                                    form.change("title", details.title);
                                    form.change(
                                      "description",
                                      details.description
                                    );
                                    setFormValues((prev) => ({
                                      ...prev,
                                      url: fullUrl,
                                      title: details.title,
                                      description: details.description,
                                    }));
                                    setSearchResults([]);
                                    setShowResults(false);
                                  }
                                }}
                              >
                                <IconButton
                                  aria-label="preview video"
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    bgcolor: "rgb(255, 255, 255)",
                                    color: "green",
                                    "&:hover": {
                                      bgcolor: "rgba(240, 26, 26, 0.35)",
                                      color: "darkgreen",
                                      transform: "scale(1.1)",
                                    },
                                    zIndex: 10,
                                    transition: "all 0.3s ease",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setModalVideoId(videoId);
                                    setModalOpen(true);
                                  }}
                                >
                                  <VisibilityIcon fontSize="medium" />
                                </IconButton>

                                <CardMedia
                                  component="img"
                                  height="140"
                                  image={thumbnail}
                                  alt={title}
                                />
                                <CardContent sx={{ p: 1 }}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {title.length > 70
                                      ? `${title.slice(0, 67)}...`
                                      : title}
                                  </Typography>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Box>

                        {nextPageToken && (
                          <Box sx={{ textAlign: "center", mt: 2 }}>
                            <Button
                              variant="outlined"
                              onClick={handleLoadMore}
                              disabled={isLoading}
                            >
                              {isLoading ? "Loading..." : "Load More"}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>

                  <Modal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    aria-labelledby="video-modal"
                    aria-describedby="video-player-modal"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: { xs: "90%", sm: 640 },
                        height: { xs: "50vh", sm: 360 },
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        borderRadius: 2,
                        outline: "none",
                      }}
                    >
                      {modalVideoId && (
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${modalVideoId}?autoplay=1&mute=0&controls=1`}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="autoplay; encrypted-media"
                          allowFullScreen
                        />
                      )}
                    </Box>
                  </Modal>

                  <Field name="url">
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        label="YouTube Video URL"
                        fullWidth
                        required
                        value={url}
                        onChange={(e) => {
                          setUrl(e.target.value);
                          input.onChange(e.target.value);
                        }}
                        onPaste={(e) => {
                          const pastedText = e.clipboardData.getData("text");
                          setTimeout(() => {
                            setUrl(pastedText);
                            input.onChange(pastedText);
                            handleScrubVideoDetails(pastedText, form);
                          }, 0);
                        }}
                        onBlur={() => {
                          if (input.value?.trim()) {
                            handleScrubVideoDetails(input.value.trim(), form);
                          }
                        }}
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                        sx={{
                          mb: 2,
                          backgroundColor: "#f4f6f8",
                          borderRadius: "8px",
                        }}
                      />
                    )}
                  </Field>

                  {videoDetails.thumbnail && (
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={videoDetails.thumbnail}
                        alt="Video Thumbnail"
                        style={{ width: "100%", borderRadius: "8px" }}
                      />
                    </Box>
                  )}

                  {(url || videoDetails.title) && (
                    <>
                      <Field name="title">
                        {({ input, meta }) => (
                          <TextField
                            {...input}
                            label="Video Title"
                            fullWidth
                            value={input.value ?? ""}
                            onChange={(e) => {
                              input.onChange(e.target.value);
                              setFormValues((prev) => ({
                                ...prev,
                                title: e.target.value,
                              }));
                            }}
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                            sx={{
                              mb: 2,
                              backgroundColor: "#f4f6f8",
                              borderRadius: "8px",
                            }}
                          />
                        )}
                      </Field>

                      <Field name="description">
                        {({ input }) => (
                          <TextField
                            {...input}
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={input.value ?? ""}
                            onChange={(e) => {
                              input.onChange(e.target.value);
                              setFormValues((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }));
                            }}
                            sx={{
                              mb: 2,
                              backgroundColor: "#f4f6f8",
                              borderRadius: "8px",
                            }}
                          />
                        )}
                      </Field>
                    </>
                  )}
                </Box>
              )}

              {values.platform === "tk" && (
                <Field name="tiktokCopyUrl">
                  {({ input, meta }) => (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        {...input}
                        label="Paste TikTok Video URL"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={input.value || ""}
                        onChange={async (e) => {
                          const value = e.target.value;
                          input.onChange(value);
                          setUrl(value);

                          if (value.includes("tiktok.com")) {
                            await handleScrubTikTokDetails(value, form);
                          }
                        }}
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />

                      {(videoDetails.title || videoDetails.thumbnail) && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            border: "1px solid #ddd",
                            borderRadius: 1,
                          }}
                        >
                          <Field name="title">
                            {({ input: titleInput, meta: titleMeta }) => (
                              <TextField
                                {...titleInput}
                                label="Title"
                                variant="outlined"
                                value={titleInput.value || ""}
                                size="small"
                                fullWidth
                                sx={{ mb: 1 }}
                                error={
                                  titleMeta.touched && Boolean(titleMeta.error)
                                }
                                helperText={
                                  titleMeta.touched && titleMeta.error
                                }
                                onChange={(e) => {
                                  titleInput.onChange(e.target.value);
                                  setFormValues((prev) => ({
                                    ...prev,
                                    title: e.target.value,
                                  }));
                                }}
                              />
                            )}
                          </Field>

                          <Field name="description">
                            {({ input: descriptionInput }) => (
                              <TextField
                                {...descriptionInput}
                                label="Description"
                                variant="outlined"
                                value={descriptionInput.value || ""}
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mb: 1 }}
                                onChange={(e) => {
                                  descriptionInput.onChange(e.target.value);
                                  setFormValues((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                  }));
                                }}
                              />
                            )}
                          </Field>

                          {videoDetails.thumbnail && (
                            <img
                              src={videoDetails.thumbnail}
                              alt="TikTok thumbnail"
                              style={{
                                width: "100%",
                                maxHeight: 650,
                                objectFit: "cover",
                                marginTop: 8,
                              }}
                            />
                          )}
                        </Box>
                      )}

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        <Button
                          onClick={() => resetAndClose(form)}
                          color="primary"
                        >
                          Cancel
                        </Button>

                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={async () => {
                            const errors = validate(form.getState().values);

                            if (errors.tiktokCopyUrl) {
                              form.change(
                                "tiktokCopyUrl",
                                form.getState().values.tiktokCopyUrl || ""
                              );
                              toast.error(errors.tiktokCopyUrl);
                              return;
                            }

                            if (errors.title) {
                              form.change(
                                "title",
                                form.getState().values.title || ""
                              );
                              toast.error("Title is required.");
                              return;
                            }

                            await handleSubmitTikTok(
                              form.getState().values,
                              form
                            );
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Field>
              )}

              {values.platform === "S3" && (
                <Box
                  sx={{
                    mb: 2,
                    p: 4,
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "600", marginBottom: "10px" }}
                  >
                    Select videos to add to your playlist
                  </Typography>

                  {s3Videos.length > 0 ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      {s3Videos.map((video) => (
                        <Card
                          key={video.url}
                          sx={{
                            cursor: "pointer",
                            position: "relative",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                            transition: "transform 0.3s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                            },
                            border: selectedVideos.includes(video.url)
                              ? "3px solid #4caf50"
                              : "none",
                          }}
                          onMouseEnter={() => handleMouseEnter(video)}
                          onMouseLeave={handleMouseLeave}
                          onClick={() => toggleSelection(video.url)}
                        >
                          {hoveredVideo === video.url ? (
                            <video
                              src={video.url}
                              width="100%"
                              height="120"
                              autoPlay
                              muted
                              loop
                              style={{
                                borderRadius: "8px",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <CardMedia
                              component="img"
                              height="120"
                              image={video.thumbnail || ""}
                              alt={video.title || ""}
                            />
                          )}

                          <CardContent>
                            <Typography variant="body2" noWrap>
                              {video.title}
                            </Typography>
                          </CardContent>

                          {selectedVideos.includes(video.url) && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "5px",
                                right: "5px",
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                borderRadius: "50%",
                                padding: "5px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CheckCircleIcon sx={{ color: "white" }} />
                            </Box>
                          )}
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ mt: 2, color: "gray" }}>
                      No videos found.
                    </Typography>
                  )}
                </Box>
              )}

              {values.platform !== "tk" && (
                <DialogActions>
                  <Button onClick={() => resetAndClose(form)} color="primary">
                    Cancel
                  </Button>
                  <Button type="submit" color="primary" variant="contained">
                    Add
                  </Button>
                </DialogActions>
              )}
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

export default PlaylistModal;